import { AppActions, AppPageFrame, baseActionCreators, Loader, OidcState } from "@interface48/app";
import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { bindActionCreators } from "redux";
import {
  CourseForm,
  CourseFormData,
  toAddCourseCommand,
  toCourseFormData,
  toUpdateCourseCommand,
} from "../../../components/courses";
import { EntityDeleteDialog } from "../../../components/shared";
import { actionCreators, AdministrationActions, AdministrationState, ApplicationState } from "../../../store";

const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
    },
    form: {
      backgroundColor: theme.palette.background.default,
    },
  });

interface CoursePageMatchParamsProps {
  courseId: string;
}

interface CoursePageStateProps {
  oidcState: OidcState;
  administrationState: AdministrationState;
}

interface CoursePageDispatchProps {
  appActions: AppActions;
  administrationActions: AdministrationActions;
}

interface CoursePageOwnProps extends RouteComponentProps<CoursePageMatchParamsProps>, WithStyles<typeof styles> {}

type CoursePageProps = CoursePageStateProps & CoursePageDispatchProps & CoursePageOwnProps;

interface CoursePageState {
  formMode: "read" | "write";
  formData: CourseFormData;
  initialFormData: CourseFormData;
  isDeleteConfirmationModalOpen: boolean;
}

export const CoursePageComponent = class extends React.Component<CoursePageProps, CoursePageState> {
  constructor(props: CoursePageProps) {
    super(props);

    const formMode = this.getCourseId() ? "read" : "write";
    const formData = toCourseFormData();

    this.state = {
      formMode,
      formData,
      initialFormData: cloneDeep(formData),
      isDeleteConfirmationModalOpen: false,
    };
  }

  public componentDidMount() {
    const { administrationActions } = this.props;

    const courseId = this.getCourseId();

    if (courseId) {
      administrationActions.requestCourse(courseId);
    }
  }

  public render() {
    const { administrationState, classes } = this.props;
    const { course, actionStatus } = administrationState;
    const { formMode, formData, isDeleteConfirmationModalOpen } = this.state;

    const formKey = `form_${formMode}`;
    const courseId = this.getCourseId();
    const isCourseLoading = !course && actionStatus.type === "ADMINISTRATION_COURSE_REQUEST" && actionStatus.pending;
    const isCourseSaving =
      (actionStatus.type === "ADMINISTRATION_COURSE_ADD_REQUEST" ||
        actionStatus.type === "ADMINISTRATION_COURSE_UPDATE_REQUEST") &&
      actionStatus.pending;
    const isCourseFormLoading = courseId && (isCourseLoading || !formData.id);

    return (
      <AppPageFrame
        pageTitle={
          !courseId
            ? "Add Course"
            : isCourseFormLoading
            ? "Loading Course..."
            : `${formMode === "write" ? "Edit " : ""}Course - ${course!.name}`
        }
        onBackClick={this.handleBackClick}
      >
        <div className={classes.container}>
          {isCourseFormLoading ? (
            <Loader loadingText={"Loading Course"} />
          ) : (
            <>
              <CourseForm
                key={formKey}
                className={classes.form}
                formMode={formMode}
                formData={formData}
                onEdit={this.handleEdit}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit}
                onCancel={this.handleCancel}
                onClose={this.handleClose}
                onDelete={this.handleDelete}
                isFormSubmitting={isCourseSaving}
              />
              {courseId && formData.name && (
                <EntityDeleteDialog
                  entityId={courseId}
                  entityName={formData.name}
                  entityDeleteOption={"Delete the Course permanently?"}
                  isOpen={isDeleteConfirmationModalOpen}
                  isActionPending={actionStatus.pending}
                  onCancel={this.handleDeleteCancel}
                  onDelete={this.handleDeleteConfirm}
                />
              )}
            </>
          )}
        </div>
      </AppPageFrame>
    );
  }

  public componentDidUpdate(prevProps: CoursePageProps, prevState: CoursePageState) {
    const { administrationState: prevAdministrationState } = prevProps;
    const { formMode: prevFormMode } = prevState;
    const { administrationState, appActions, administrationActions, history } = this.props;
    const { course } = administrationState;
    const { formMode } = this.state;

    const courseId = this.getCourseId();

    const prevActionStatus = prevAdministrationState.actionStatus;
    const actionStatus = administrationState.actionStatus;

    // If some Course action just completed...
    if (prevActionStatus.pending && !actionStatus.pending) {
      // If an error occurred, display the message...
      if (actionStatus.error) {
        const errorMessage =
          actionStatus.error.message || "An unexpected error occurred upon attempting to process the last operation.";

        appActions.postSnackbarMessage(errorMessage, { variant: "error" });
      }
      // Otherwise, the operation completed successfully...
      else if (course) {
        // If we fetched the Details for this Course...
        if (actionStatus.type === "ADMINISTRATION_COURSE_REQUEST" && course) {
          const formData = toCourseFormData(course);

          this.setState({ ...this.state, formMode: "read", formData });
          // If we loaded the details for a Course that was just added via /add, replace the URL with it's
          // ID-specific one...
          if (!courseId) {
            history.replace(`/administration/courses/${formData.id}`);
          }
        }
        // Otherwise if we added or updated a Course, fetch the latest Details...
        else if (
          actionStatus.type === "ADMINISTRATION_COURSE_ADD_REQUEST" ||
          actionStatus.type === "ADMINISTRATION_COURSE_UPDATE_REQUEST"
        ) {
          administrationActions.requestCourse(course.id);
        }
        // Otherwise, if we removed a Course...
        else if (actionStatus.type === "ADMINISTRATION_COURSE_DELETE_REQUEST") {
          history.push("/administration/courses");
        }
      }
    }

    // If the Form Mode just changed, scroll to the top of the page...
    if (prevFormMode !== formMode) {
      const appFrame = document.getElementById("app-frame");
      if (appFrame) {
        appFrame.scrollTop = 0;
      }
    }
  }

  private handleBackClick = () => {
    this.props.history.goBack();
  };

  private handleClose = () => {
    this.props.history.goBack();
  };

  private handleEdit = () => {
    this.setState({ ...this.state, formMode: "write" });
  };

  private handleCancel = (initialFormData: CourseFormData) => {
    const { history } = this.props;

    if (!this.getCourseId()) {
      history.push("/administration/courses");
    } else {
      this.setState({ ...this.state, formMode: "read", formData: initialFormData });
    }
  };

  private handleChange = (formData: CourseFormData) => {
    this.setState({ ...this.state, formData });
  };

  private handleSubmit = (formData: CourseFormData) => {
    const { administrationActions } = this.props;

    // If adding a new Course...
    if (!this.getCourseId()) {
      const addCourseCommand = toAddCourseCommand(formData);

      administrationActions.requestCourseAdd(addCourseCommand);
    }
    // Otherwise, if updating a Course...
    else {
      if (!formData.id) {
        throw Error("Course Id is undefined upon request to update.");
      }

      const updateCourseCommand = toUpdateCourseCommand(formData);

      administrationActions.requestCourseUpdate(formData.id, updateCourseCommand);
    }
  };

  private handleDelete = () => {
    this.setState({ ...this.state, isDeleteConfirmationModalOpen: true });
  };

  private handleDeleteCancel = () => {
    this.setState({ ...this.state, isDeleteConfirmationModalOpen: false });
  };

  private handleDeleteConfirm = (courseId: string, permanentlyDeleteFile?: boolean) => {
    const { administrationActions } = this.props;

    administrationActions.requestCourseDelete(courseId, permanentlyDeleteFile ?? false);
  };

  private getCourseId = (): string | undefined => {
    const { match } = this.props;
    const { params } = match;
    const { courseId } = params;

    if (courseId.toLowerCase() === "add") {
      return undefined;
    }

    return courseId;
  };
};

export const CoursePage = withStyles(styles)(
  connect<CoursePageStateProps, CoursePageDispatchProps, CoursePageOwnProps, ApplicationState>(
    (state) => ({
      oidcState: state.oidc,
      administrationState: state.administration,
    }),
    (dispatch) => ({
      appActions: bindActionCreators(baseActionCreators.app, dispatch),
      administrationActions: bindActionCreators(actionCreators.administration, dispatch),
    }),
  )(CoursePageComponent),
);
